'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { motion, useAnimation } from 'framer-motion';

interface IconProps {
	isHovered?: boolean;
}

const faceVariants = {
	normal: {
		scale: 1,
		rotate: 0,
		transition: {
			duration: 0.3,
			ease: 'easeOut',
		},
	},
	animate: {
		scale: [1, 1.15, 1.05, 1.08],
		rotate: [0, -2, 2, 0],
		transition: {
			duration: 0.8,
			times: [0, 0.3, 0.6, 1],
			ease: 'easeInOut',
		},
	},
};

const mouthVariants = {
	normal: {
		d: 'M16 16s-1.5-2-4-2-4 2-4 2',
		pathLength: 1,
		transition: {
			duration: 0.3,
			ease: 'easeOut',
		},
	},
	animate: {
		d: 'M16 17s-1.5-2.5-4-2.5-4 2.5-4 2.5',
		pathLength: [0.3, 1, 1],
		transition: {
			d: { duration: 0.5, ease: 'easeOut' },
			pathLength: {
				duration: 0.5,
				times: [0, 0.5, 1],
				ease: 'easeInOut',
			},
			delay: 0.1,
		},
	},
};

const leftEyeVariants = {
	normal: {
		scale: 1,
		y: 0,
		transition: {
			duration: 0.3,
			ease: 'easeOut',
		},
	},
	animate: {
		scale: [1, 1.3, 0.9, 1.1],
		y: [0, -0.5, 0.3, 0],
		transition: {
			duration: 0.6,
			times: [0, 0.3, 0.6, 1],
			ease: 'easeInOut',
		},
	},
};

const rightEyeVariants = {
	normal: {
		scale: 1,
		y: 0,
		transition: {
			duration: 0.3,
			ease: 'easeOut',
		},
	},
	animate: {
		scale: [1, 0.9, 1.3, 1.1],
		y: [0, -0.5, 0.3, 0],
		transition: {
			duration: 0.6,
			times: [0, 0.3, 0.6, 1],
			ease: 'easeInOut',
		},
	},
};

export function FrownIcon({ isHovered }: IconProps) {
	const controls = useAnimation();

	React.useEffect(() => {
		if (isHovered) {
			controls.start('animate');
		} else {
			controls.start('normal');
		}
	}, [isHovered, controls]);

	return (
		<div className='select-none p-1 flex items-center justify-center'>
			<motion.svg
				xmlns='http://www.w3.org/2000/svg'
				width='50'
				height='50'
				viewBox='-4 -4 32 32'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
				className={cn(
					'transition-colors text-white',
					isHovered && 'text-yellow-300'
				)}
				variants={faceVariants}
				initial='normal'
				animate={controls}
			>
				<g>
					<circle cx='12' cy='12' r='10' />
					<motion.path
						variants={mouthVariants}
						animate={controls}
						initial='normal'
						d='M16 16s-1.5-2-4-2-4 2-4 2'
					/>
					<motion.line
						x1='9'
						x2='9.01'
						y1='9'
						y2='9'
						variants={leftEyeVariants}
						animate={controls}
						initial='normal'
					/>
					<motion.line
						x1='15'
						x2='15.01'
						y1='9'
						y2='9'
						variants={rightEyeVariants}
						animate={controls}
						initial='normal'
					/>
				</g>
			</motion.svg>
		</div>
	);
}
